import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { fetchToolSettings } from "../services/ToolSettingsServices";
import { useOverviewContext } from "./OverviewContext";
import { useSelectedStudent } from "./SelectedStudentContext";

interface ToolSettings {
  [toolID: number]: boolean; // Stores toolID as key and visibility (status === 1) as value
}

interface ToolSettingsContextType {
  toolSettings: ToolSettings;
  /** `true` after fetch completes or is skipped (missing token / school / role). */
  toolSettingsLoaded: boolean;
}

const ToolSettingsContext = createContext<ToolSettingsContextType | undefined>(undefined);

export const ToolSettingsProvider = ({ children }: { children: ReactNode }) => {
  const { authState } = useOktaAuth();
  const { userRoleId } = useOverviewContext();
  const { selectedStudent } = useSelectedStudent();
  const [toolSettings, setToolSettings] = useState<ToolSettings>({});
  const [toolSettingsLoaded, setToolSettingsLoaded] = useState(false);
  const oktaAccessToken = authState?.accessToken?.accessToken;
  const schoolId = selectedStudent?.schoolId ?? "";
  const resolvedRoleId = userRoleId ?? selectedStudent?.roleId ?? null;
  const roleIdSegment = resolvedRoleId != null ? String(resolvedRoleId) : "";

  useEffect(() => {
    if (!oktaAccessToken || !schoolId || !roleIdSegment) {
      if (import.meta.env.DEV) {
        console.debug(
          "[toolSettings] GET skipped (need token, schoolId, role id from user or student):",
          {
            hasToken: Boolean(oktaAccessToken),
            schoolId: schoolId || "(empty)",
            userRoleId,
            studentRoleId: selectedStudent?.roleId,
          }
        );
      }
      setToolSettings({});
      setToolSettingsLoaded(true);
      return;
    }

    let cancelled = false;
    setToolSettingsLoaded(false);

    const getToolSettings = async () => {
      try {
        const data = await fetchToolSettings(oktaAccessToken, schoolId, roleIdSegment);
        if (cancelled) return;

        const settingsMap: ToolSettings = {};
        data.forEach((tool: { toolID: number; status: number }) => {
          settingsMap[tool.toolID] = tool.status === 1;
        });

        setToolSettings(settingsMap);
      } finally {
        if (!cancelled) setToolSettingsLoaded(true);
      }
    };

    void getToolSettings();

    return () => {
      cancelled = true;
    };
  }, [oktaAccessToken, schoolId, roleIdSegment, userRoleId, selectedStudent?.roleId]);
  return (
    <ToolSettingsContext.Provider value={{ toolSettings, toolSettingsLoaded }}>
      {children}
    </ToolSettingsContext.Provider>
  );
};

export const useToolSettings = () => {
  const context = useContext(ToolSettingsContext);
  if (!context) {
    throw new Error("useToolSettings must be used within a ToolSettingsProvider");
  }
  return context;
};
